import _ from 'lodash';

import { useGetAgents } from './use-get-agents';
import {
  useAppLauncherStore,
  useAppLauncherSetters,
} from './use-app-launcher-store';
import { useAppLauncherRouting } from './use-app-launcher-routing';
import { AppLauncherSettings } from '../types';
import { SDK, APP_KEYS, APP_STATES, DEFAULT_APP_SETTINGS } from '../constants';
import { SETTINGS_CATEGORIES, LOCAL_STORAGE_KEYS } from '@/constants';
import { useLocalSettings } from '@/composables/use-local-settings';

export const useInitializeAppLauncher = () => {
  const store = useAppLauncherStore();

  const {
    goTo,
    goToAppBundle,
    goToAppLauncher,
    getAppByActiveRoute,
  } = useAppLauncherRouting();
  const {
    initializeActiveAgent,
    initializeHITLUpdateStatus,
    initializeHITLMigrations,
    initializeAppLauncherSettings,
    initializeMigrationsOptions,
    saveAppLauncherSettings,
  } = useInitializeData();

  /** A complete initialization chain based on agent's properties, HITL and update status */
  const initializeAppLauncher = async (args: {
    userId: string;
    multiUserId: string;
    /** We might want to do some additional initialization based on settings */
    onSettingsReadyCallback?: () => void;
  }) => {
    try {
      store.$reset();
      store.isInitializing = true;
      goTo({ state: APP_STATES.DEFAULT });

      await Promise.all([
        initializeActiveAgent(args),
        initializeHITLUpdateStatus(),
        initializeHITLMigrations(),
      ]);

      /* Users have to refresh the page when they are upgraded to agents */
      if (!store.canUserInteractWithHITL) {
        store.isInitializing = false;
        return goTo({ state: APP_STATES.NO_AGENT_ACCESS });
      }

      return store.isUserBasicAgent
        ? await initializeBasicAgentBranch(args)
        : await initializeAdminBranch(args);
    } catch (e: any) {
      store.error = e;
      store.isInitializing = false;
    }
  };

  /** Basic agents either go to Agent UI or wait until app is set up */
  const initializeBasicAgentBranch = async (options: {
    onSettingsReadyCallback?: () => void;
  }) => {
    /* Agent should wait until "Update Tracker" navigates him to next route */
    if (store.update.error) {
      store.isInitializing = false;
      return goTo({ state: APP_STATES.DATA_UPDATE_ERROR_AGENT });
    }

    if (store.isUpdateInProgress) {
      store.isInitializing = false;
      return goTo({ state: APP_STATES.APP_IS_UPDATING });
    }

    if (store.canUseSettingsAPI) {
      await initializeAppLauncherSettings();
      options.onSettingsReadyCallback?.();
    }

    /* Basic agents depend on app version set up by the admin */
    store.isInitializing = false;
    return store.isAppLauncherSetUp
      ? goToAppBundle({ app: APP_KEYS.AGENT_UI, version: store.readyToUseVersion })
      : goTo({ state: APP_STATES.NO_AGENT_ACCESS });
  };

  /** Admins either go to Agent UI (if everything is already set up) or can update or configure HITL */
  const initializeAdminBranch = async (options: {
    onSettingsReadyCallback?: () => void;
  }) => {
    /* Allow admin to retry update or accept suggested compatible version */
    if (store.update.error) {
      await initializeMigrationsOptions();

      store.isInitializing = false;
      return goTo({ state: APP_STATES.DATA_UPDATE_ERROR_ADMIN });
    }

    /* Show update progress and let "Update Tracker" decide what's next */
    if (store.isUpdateInProgress) {
      store.isInitializing = false;
      return goTo({ state: APP_STATES.APP_IS_UPDATING });
    }

    /* Suggest admin to install the latest version */
    if (!store.isAnyMigrationDone) {
      await initializeMigrationsOptions();

      const state = store.maxVersion
        ? APP_STATES.NO_APP_AVAILABLE
        : APP_STATES.NO_AGENT_ACCESS;

      store.isInitializing = false;
      return goTo({ state });
    }

    /* A bit different UI page but same suggestion */
    if (!store.isAppLauncherMigrationDone) {
      await initializeMigrationsOptions();

      const state = store.maxVersion
        ? APP_STATES.NO_APP_VERSION_SUPPORT
        : APP_STATES.NO_AGENT_ACCESS;

      store.isInitializing = false;
      return goTo({ state });
    }

    /* If there are HITL tables in the account check if launcher is already set up */
    if (store.canUseSettingsAPI) {
      await initializeAppLauncherSettings({ ignoreRuleTags: true });
      options.onSettingsReadyCallback?.();
    }

    /* Suggest admin a version of "Agent UI" compatible with current DB version */
    if (!store.isAppLauncherSetUp) {
      await initializeMigrationsOptions();

      const state = store.maxVersionByActiveMigration
        ? APP_STATES.NO_APP_VERSION
        : store.maxVersion
          /* If there is no compatible version suggest installing the latest version */
          ? APP_STATES.NO_APP_VERSION_SUPPORT
          : APP_STATES.NO_AGENT_ACCESS;

      store.isInitializing = false;
      return goTo({ state });
    }

    /* After successful update save target version in settings when opening Launcher */
    if (store.updateTargetVersion && !store.settings.appVersion) {
      await saveAppLauncherSettings({ appVersion: store.updateTargetVersion });
    }

    const app = getAppByActiveRoute();

    /* Proceed to main App Launcher settings */
    if (!app) {
      store.isInitializing = false;
      return goToAppLauncher();
    }

    /* Even when user is Admin they need to have Agent permissions to access Agent UI */
    if (store.isUserAgent || app !== APP_KEYS.AGENT_UI) {
      store.isInitializing = false;
      return goToAppBundle({ app });
    }

    /* If user is not an agent (has no rule groups), redirect to no agent access state */
    console.warn('Admin is unable to interact with App Launcher!');
    store.isInitializing = false;
    goTo({ state: APP_STATES.NO_AGENT_ACCESS });
  };

  return { initializeAppLauncher };
};

export const useInitializeData = () => {
  const store = useAppLauncherStore();
  const setters = useAppLauncherSetters();
  const { getAgent } = useGetAgents();
  const { getSettingsWithCache, getCachedSettings, setCachedSettings } = useLocalSettings();

  const initializeAppLauncherSettings = async (options: {
    ignoreRuleTags?: boolean;
  } = {}) => {
    // Use optimized caching that returns immediately if cached data exists
    const settings = await getSettingsWithCache({
      key: LOCAL_STORAGE_KEYS.LAUNCHER_SETTINGS,
      apiRequest: async () => getAPIAppLauncherSettings(options),
      onCacheMismatch() {
        window.location.reload();
      },
    });
    setters.setAppSettings(settings);
  };

  /** Check if HITL is updating or is in "update error" state */
  const initializeHITLUpdateStatus = async () => {
    const statusData = await getSettingsWithCache({
      key: LOCAL_STORAGE_KEYS.UPDATE_STATUS,
      apiRequest: async () => {
        const response = await SDK.HITL.migrations.getRunMigrationsStatus();
        const { complete, remaining: pending, meta, reports, error } = response || {};
        return { complete, pending, meta, reports, error };
      },
      onCacheMismatch() {
        window.location.reload();
      },
    });
    setters.changeUpdateProgress(statusData);
  };

  /** Get list of migrations that are already applied  */
  const initializeHITLMigrations = async () => {
    const migrations = await getSettingsWithCache({
      key: LOCAL_STORAGE_KEYS.MIGRATIONS,
      apiRequest: async () => {
        const response = await SDK.HITL.migrations.getListOfMigrations();
        return response.executed.map(item => item.name);
      },
      onCacheMismatch() {
        window.location.reload();
      },
    });
    setters.setActiveMigrations(migrations);
  };

  /** Request available in S3 versions and UI-to-DB versions map */
  const initializeMigrationsOptions = async () => {
    const responses = await Promise.all([
      SDK.HITL.versions.getVersionsList({}),
      SDK.HITL.migrations.getMigrationsInfo(),
    ]);

    store.versions = responses[0].data;
    store.migrationsByRange = responses[1].migrationsByAppVersion;
  };

  const initializeActiveAgent = async (args: {
    userId: string;
    multiUserId: string;
  }) => {
    const { userId, multiUserId } = args;

    const agent = await getSettingsWithCache({
        key: LOCAL_STORAGE_KEYS.AGENT_DATA,
        apiRequest: async () => getAgent({ userId, multiUserId }),
        filterForCache: (agent) => {
          agent = _.cloneDeep(agent);
          delete (agent as any).statusData;
          return agent;
        },
        onCacheMismatch: (fresh, cached) => {
          // reload immediately to avoid broken application state
          if (!_.isEmpty(cached?.ruleGroupIds) && _.isEmpty(fresh?.ruleGroupIds)) window.location.reload();
        },
        onBackgroundRefreshComplete(agent) {
          store.agent = agent;
        },
    });
    store.agent = agent;
  };

  /** Return custom settings merged with default settings */
  const getAPIAppLauncherSettings = async (options: {
    ignoreRuleTags?: boolean;
  } = {}) => {
    const response = await SDK.HITL.settings.getSettings({
      categories: [SETTINGS_CATEGORIES.LAUNCHER],
      isCommon: true,
      ignoreRuleTags: options.ignoreRuleTags,
    });

    const settings = response.data[SETTINGS_CATEGORIES.LAUNCHER];
    const data = _.get(settings, 'data', {});
    return { ..._.cloneDeep(DEFAULT_APP_SETTINGS), ...data } as AppLauncherSettings;
  };

  /** Save settings without any side effects like UI loaders */
  const saveAPIAppLauncherSettings = async (settings: AppLauncherSettings) => {
    const data = { [SETTINGS_CATEGORIES.LAUNCHER]: settings };
    await SDK.HITL.settings.updateSettings({ data, isCommon: true });

    // Update the cached LAUNCHER_SETTINGS with the new settings
    setCachedSettings(LOCAL_STORAGE_KEYS.LAUNCHER_SETTINGS, settings);
  };

  /** Save settings in the database without additional UI loaders */
  const saveAppLauncherSettings = async (settings: Partial<AppLauncherSettings>) => {
    const update = { ...store.settings, ...settings };
    await saveAPIAppLauncherSettings(update);

    store.settings = _.cloneDeep(update);
    store.settingsInDB = _.cloneDeep(update);
  };

  return {
    initializeActiveAgent,
    initializeHITLUpdateStatus,
    initializeHITLMigrations,
    initializeAppLauncherSettings,
    initializeMigrationsOptions,
    getAPIAppLauncherSettings,
    saveAPIAppLauncherSettings,
    saveAppLauncherSettings,
  };
};
